<template>
  <div class="rider-today">
    <el-card shadow="never" class="today-card">
      <template slot="header">
        <div class="card-header">
          <div>
            <div class="card-title">今日配送</div>
            <div class="card-sub">
              {{ summary.date || todayLabel }} · {{ windowLabel }} · 共 {{ summary.total || 0 }} 单 · 可开始 {{ summary.startable || 0 }} 单
            </div>
            <div class="card-hint auto-deliver-hint">
              到时自动配送：午餐 {{ autoSchedule.lunch_auto_deliver_at || '10:30' }} · 晚餐 {{ autoSchedule.dinner_auto_deliver_at || '17:30' }}
            </div>
            <div v-if="windowHint" class="card-hint">{{ windowHint }}</div>
          </div>
          <div class="card-actions">
            <el-select
              v-model="filterRegion"
              clearable
              placeholder="全部配送区域"
              size="small"
              style="width: 180px; margin-right: 12px;"
              @change="fetchOrders"
            >
              <el-option v-for="r in deliveryRegionOptions" :key="r" :label="r" :value="r" />
            </el-select>
            <el-button size="small" @click="fetchOrders">刷新</el-button>
            <el-button
              type="primary"
              size="small"
              :loading="batchLoading"
              :disabled="!summary.startable"
              @click="batchStart"
            >
              一键开始配送
            </el-button>
          </div>
        </div>
      </template>

      <el-table v-loading="loading" :data="orders" size="small" stripe class="today-table">
        <el-table-column prop="order_no" label="订单号" min-width="150" show-overflow-tooltip />
        <el-table-column prop="order_status" label="状态" width="96">
          <template slot-scope="scope">
            <el-tag
              size="mini"
              :type="statusTagType(scope.row.order_status)"
              :class="{ 'tag-overdue': scope.row.order_status === '已超时' }"
            >
              {{ scope.row.order_status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="订单时间" min-width="130" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.delivery_time_text || (scope.row.delivery_date ? scope.row.delivery_date + ' ' + (scope.row.meal_slot || '') : scope.row.meal_slot) }}
          </template>
        </el-table-column>
        <el-table-column prop="delivery_region" label="配送区域" width="110" show-overflow-tooltip />
        <el-table-column prop="user_name" label="收货人" width="100" show-overflow-tooltip />
        <el-table-column prop="user_phone" label="电话" width="120" />
        <el-table-column prop="user_address" label="地址" min-width="180" show-overflow-tooltip />
        <el-table-column prop="meal_slot" label="餐次" width="88" show-overflow-tooltip />
        <el-table-column prop="rider_name" label="骑手" width="100" show-overflow-tooltip />
        <el-table-column label="操作" width="320" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.can_start"
              type="primary"
              size="mini"
              plain
              :loading="actionLoadingId === scope.row.id && actionType === 'start'"
              @click="doAction(scope.row, 'start')"
            >
              开始配送
            </el-button>
            <el-button
              v-if="scope.row.can_complete"
              type="success"
              size="mini"
              plain
              :loading="actionLoadingId === scope.row.id && actionType === 'complete'"
              @click="doAction(scope.row, 'complete')"
            >
              确认送达
            </el-button>
            <el-button
              v-if="scope.row.can_redeliver"
              type="warning"
              size="mini"
              plain
              :loading="actionLoadingId === scope.row.id && actionType === 'redeliver'"
              @click="doAction(scope.row, 'redeliver')"
            >
              重新配送
            </el-button>
            <el-button
              v-if="scope.row.can_cancel"
              type="danger"
              size="mini"
              plain
              :loading="actionLoadingId === scope.row.id && actionType === 'cancel'"
              @click="doAction(scope.row, 'cancel')"
            >
              取消配送
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!loading && !orders.length" class="empty-tip">{{ emptyTip }}</div>
    </el-card>
  </div>
</template>

<script>
import axios from 'axios'
import { getAdminAuthHeader, getAdminToken } from '../utils/auth'

function riderAuthCfg (extra = {}) {
  return {
    ...extra,
    headers: { ...(extra.headers || {}), ...getAdminAuthHeader() }
  }
}

function chinaHM () {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Shanghai',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date())
    const hourPart = parts.find((p) => p.type === 'hour')
    const minutePart = parts.find((p) => p.type === 'minute')
    const h = (hourPart && hourPart.value) || '00'
    const m = (minutePart && minutePart.value) || '00'
    return `${h}:${m}`
  } catch (e) {
    return ''
  }
}

export default {
  name: 'RiderTodayDelivery',
  data () {
    return {
      loading: false,
      batchLoading: false,
      actionLoadingId: null,
      actionType: '',
      filterRegion: '',
      deliveryRegionOptions: [],
      orders: [],
      summary: { date: '', total: 0, startable: 0 },
      windowInfo: null,
      autoSchedule: {
        lunch_auto_deliver_at: '10:30',
        dinner_auto_deliver_at: '17:30'
      },
      refreshTimer: null,
      boundaryTimer: null,
      lastBoundaryKey: '',
      deliveryWs: null
    }
  },
  computed: {
    todayLabel () {
      try {
        return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Shanghai' }).format(new Date())
      } catch (e) {
        return ''
      }
    },
    windowLabel () {
      return (this.windowInfo && this.windowInfo.label) || '加载中…'
    },
    windowHint () {
      return (this.windowInfo && this.windowInfo.window_text) || ''
    },
    emptyTip () {
      if (this.windowInfo && this.windowInfo.phase === 'lunch') {
        return '午餐订单须在 10:30 前打印才会出现在此列表'
      }
      if (this.windowInfo && this.windowInfo.phase === 'dinner') {
        return '晚餐订单须在 16:30 前打印才会出现在此列表'
      }
      return '当前餐次暂无配送订单'
    }
  },
  mounted () {
    this.fetchRegions()
    this.fetchAutoSchedule()
    this.fetchOrders()
    this.connectDeliveryWebSocket()
    this.refreshTimer = setInterval(() => {
      this.fetchOrders(true)
    }, 60 * 1000)
    this.boundaryTimer = setInterval(() => {
      this.checkMealBoundary()
    }, 15 * 1000)
  },
  beforeDestroy () {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    if (this.boundaryTimer) clearInterval(this.boundaryTimer)
    if (this.deliveryWs) {
      try {
        this.deliveryWs.close()
      } catch (e) { /* ignore */ }
      this.deliveryWs = null
    }
  },
  methods: {
    connectDeliveryWebSocket () {
      try {
        const token = getAdminToken() || ''
        if (!token) return
        if (this.deliveryWs) {
          try {
            this.deliveryWs.close()
          } catch (e) { /* ignore */ }
          this.deliveryWs = null
        }
        const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
        const host = window.location.host
        const url = `${proto}://${host}/ws/meal-sync?token=${encodeURIComponent(token)}`
        const ws = new WebSocket(url)
        ws.onerror = () => { /* ignore */ }
        ws.onclose = () => {
          if (this.deliveryWs === ws) this.deliveryWs = null
        }
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data)
            if (!msg) return
            if (['delivery_ready', 'delivery_complete', 'delivery_cancel'].includes(msg.type)) {
              this.fetchOrders(true)
            }
          } catch (e) { /* ignore */ }
        }
        this.deliveryWs = ws
      } catch (e) { /* ignore */ }
    },
    checkMealBoundary () {
      const hm = chinaHM()
      if (hm === '10:30' || hm === '16:30') {
        const key = `${this.todayLabel}-${hm}`
        if (this.lastBoundaryKey !== key) {
          this.lastBoundaryKey = key
          this.fetchOrders(true)
        }
      }
    },
    async fetchRegions () {
      try {
        const resp = await axios.get('/api/admin/delivery-regions', riderAuthCfg())
        if (resp.data && resp.data.code === 200 && Array.isArray(resp.data.data)) {
          this.deliveryRegionOptions = resp.data.data.map((x) => (typeof x === 'string' ? x : x.label)).filter(Boolean)
        }
      } catch (e) {
        this.deliveryRegionOptions = []
      }
    },
    async fetchAutoSchedule () {
      try {
        const resp = await axios.get('/api/admin/rider/auto-delivery-schedule', riderAuthCfg())
        if (resp.data && resp.data.code === 200 && resp.data.data) {
          this.autoSchedule = Object.assign({}, this.autoSchedule, resp.data.data)
        }
      } catch (e) {
        /* keep defaults */
      }
    },
    async fetchOrders (silent) {
      if (!silent) this.loading = true
      try {
        const params = {}
        if (this.filterRegion) params.delivery_region = this.filterRegion
        const resp = await axios.get('/api/riders/today/deliveries', riderAuthCfg({ params }))
        if (resp.data && resp.data.code === 200 && resp.data.data) {
          const d = resp.data.data
          this.orders = d.orders || []
          this.windowInfo = d.window || null
          this.summary = {
            date: d.date,
            total: d.total,
            startable: d.startable
          }
        }
      } catch (e) {
        if (!silent) this.$message.error('加载今日配送订单失败')
      } finally {
        if (!silent) this.loading = false
      }
    },
    statusTagType (status) {
      const s = String(status || '')
      if (s === '已超时') return 'danger'
      if (s === '配送中') return 'warning'
      if (s === '待配送' || s === '进行中' || s === '待发货') return 'info'
      if (s === '配送完成' || s === '已完成') return 'success'
      if (s === '已取消') return 'danger'
      return ''
    },
    async batchStart () {
      try {
        await this.$confirm('确认将当前餐次可配送订单全部标记为「配送中」？', '一键开始配送', {
          type: 'warning'
        })
      } catch (e) {
        return
      }
      this.batchLoading = true
      try {
        const body = {}
        if (this.filterRegion) body.delivery_region = this.filterRegion
        const resp = await axios.post('/api/riders/today/deliveries/batch-start', body, riderAuthCfg())
        this.$message.success((resp.data && resp.data.message) || '操作成功')
        this.fetchOrders()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || '操作失败'
        this.$message.error(msg)
      } finally {
        this.batchLoading = false
      }
    },
    async doAction (row, action) {
      const labels = {
        start: '开始配送',
        complete: '确认送达',
        redeliver: '重新配送',
        cancel: '取消配送'
      }
      try {
        await this.$confirm(`确认对订单 ${row.order_no}（${row.meal_slot}）执行「${labels[action]}」？`, '提示', { type: 'warning' })
      } catch (e) {
        return
      }
      this.actionLoadingId = row.id
      this.actionType = action
      try {
        const resp = await axios.post(
          `/api/riders/today/deliveries/${row.id}/action`,
          { action },
          riderAuthCfg()
        )
        this.$message.success((resp.data && resp.data.message) || '操作成功')
        this.fetchOrders()
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || '操作失败'
        this.$message.error(msg)
      } finally {
        this.actionLoadingId = null
        this.actionType = ''
      }
    }
  }
}
</script>

<style scoped>
.rider-today {
  padding: 24px;
  background: #f5f7fa;
  min-height: calc(100vh - 80px);
}

.today-card {
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.06);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}

.card-sub {
  margin-top: 4px;
  font-size: 13px;
  color: #86909c;
}

.card-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #f53f3f;
}

.card-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-overdue {
  background-color: #f53f3f !important;
  border-color: #f53f3f !important;
  color: #fff !important;
}

.empty-tip {
  text-align: center;
  color: #909399;
  padding: 32px 0 12px;
  font-size: 14px;
}
</style>
